/**
 * Service Dashboard Unifié Frontend
 * Phase 3.2 - Optimisation avec réutilisation maximale
 */

import api from './api';
import { DASHBOARD_TYPES, STATS_PERIODS } from '../constants/dashboardConstants';

class DashboardUnifiedService {
  
  /**
   * Récupérer le dashboard propriétaire complet
   */
  async getOwnerDashboard(options = {}) {
    try {
      const { period = STATS_PERIODS.MONTH, customStart, customEnd, widgets } = options;
      
      const params = new URLSearchParams({
        period,
        ...(customStart && { custom_start: customStart }),
        ...(customEnd && { custom_end: customEnd }),
        ...(widgets && { widgets: widgets.join(',') })
      });

      console.log('🔥 API Call: GET /dashboard/owner', { params: params.toString() });
      const response = await api.get(`/dashboard/owner?${params}`);
      console.log('🔥 RÉPONSE API OWNER:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur dashboard propriétaire:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer le dashboard locataire complet
   */
  async getRenterDashboard(options = {}) {
    try {
      const { period = STATS_PERIODS.MONTH, customStart, customEnd, widgets } = options;
      
      const params = new URLSearchParams({
        period,
        ...(customStart && { custom_start: customStart }),
        ...(customEnd && { custom_end: customEnd }),
        ...(widgets && { widgets: widgets.join(',') })
      });

      console.log('API Call: GET /dashboard/renter', { params: params.toString() });
      const response = await api.get(`/dashboard/renter?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erreur dashboard locataire:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer le dashboard automatique
   */
  async getAutoDashboard(options = {}) {
    try {
      const { period = STATS_PERIODS.MONTH, customStart, customEnd } = options;
      
      const params = new URLSearchParams({
        period,
        ...(customStart && { custom_start: customStart }),
        ...(customEnd && { custom_end: customEnd })
      });

      console.log('API Call: GET /dashboard/auto', { params: params.toString() });
      const response = await api.get(`/dashboard/auto?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erreur dashboard automatique:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer un widget spécifique
   */
  async getWidget(type, widgetName, options = {}) {
    try {
      const { period = STATS_PERIODS.MONTH, customStart, customEnd } = options;
      
      const params = new URLSearchParams({
        period,
        ...(customStart && { custom_start: customStart }),
        ...(customEnd && { custom_end: customEnd })
      });

      console.log(`API Call: GET /dashboard/${type}/widget/${widgetName}`, { params: params.toString() });
      const response = await api.get(`/dashboard/${type}/widget/${widgetName}?${params}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur widget ${widgetName}:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Récupérer les métriques rapides
   */
  async getMetrics(type, options = {}) {
    try {
      const { period = STATS_PERIODS.MONTH } = options;
      
      const params = new URLSearchParams({ period });

      console.log(`API Call: GET /dashboard/${type}/metrics`, { params: params.toString() });
      const response = await api.get(`/dashboard/${type}/metrics?${params}`);
      return response.data;
    } catch (error) {
      console.error('Erreur métriques:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Méthodes de compatibilité avec l'ancien système
   * Réutilise les nouvelles API mais garde l'interface existante
   */

  /**
   * LEGACY: Récupérer les préférences utilisateur (réutilise le service existant)
   */
  async getUserPreferences() {
    try {
      console.log('API Call: GET /dashboard/preferences');
      const response = await api.get('/dashboard/preferences');
      return response.data.preferences;
    } catch (error) {
      console.error('Erreur préférences utilisateur:', error);
      return this.getDefaultPreferences();
    }
  }

  /**
   * LEGACY: Sauvegarder les préférences utilisateur
   */
  async saveUserPreferences(preferences) {
    try {
      console.log('API Call: POST /dashboard/preferences', preferences);
      const response = await api.post('/dashboard/preferences', preferences);
      return response.data.preferences;
    } catch (error) {
      console.error('Erreur sauvegarde préférences:', error);
      throw this.handleError(error);
    }
  }

  /**
   * LEGACY: Réinitialiser les préférences
   */
  async resetToDefault() {
    try {
      console.log('API Call: POST /dashboard/preferences/reset');
      const response = await api.post('/dashboard/preferences/reset');
      return response.data.preferences;
    } catch (error) {
      console.error('Erreur réinitialisation préférences:', error);
      return this.getDefaultPreferences();
    }
  }

  /**
   * LEGACY: Mettre à jour la disposition des widgets
   */
  async updateWidgetsLayout(widgetsLayout) {
    try {
      console.log('API Call: PATCH /dashboard/preferences/widgets', { widgets: widgetsLayout });
      const response = await api.patch('/dashboard/preferences/widgets', { widgets: widgetsLayout });
      return response.data.preferences;
    } catch (error) {
      console.error('Erreur mise à jour widgets:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Utilitaires
   */
  getDefaultPreferences() {
    return {
      layout: 'grid',
      theme: 'light',
      widgets: [],
      colors: {},
      notifications: {
        email: true,
        push: true,
        sms: false
      }
    };
  }

  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.message) {
      return new Error(error.message);
    }
    return new Error('Une erreur est survenue');
  }

  /**
   * Helper pour construire les options de période
   */
  buildPeriodOptions(period, customStart = null, customEnd = null) {
    const options = { period };
    
    if (period === STATS_PERIODS.CUSTOM && customStart && customEnd) {
      options.customStart = customStart;
      options.customEnd = customEnd;
    }
    
    return options;
  }

  /**
   * Helper pour valider les paramètres de dashboard
   */
  validateDashboardParams(type, options = {}) {
    if (!Object.values(DASHBOARD_TYPES).includes(type)) {
      throw new Error(`Type de dashboard invalide: ${type}`);
    }

    if (options.period && !Object.values(STATS_PERIODS).includes(options.period)) {
      throw new Error(`Période invalide: ${options.period}`);
    }

    if (options.period === STATS_PERIODS.CUSTOM) {
      if (!options.customStart || !options.customEnd) {
        throw new Error('Les dates de début et fin sont requises pour une période personnalisée');
      }
      
      const start = new Date(options.customStart);
      const end = new Date(options.customEnd);
      
      if (start >= end) {
        throw new Error('La date de début doit être antérieure à la date de fin');
      }
    }

    return true;
  }
}

// Export singleton
const dashboardUnifiedService = new DashboardUnifiedService();
export default dashboardUnifiedService;
